import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import styled from '@emotion/styled';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useViewClaimDocument } from '../../hooks/useViewClaimDocument';
import { fetchMissingPromotions } from '../../redux/actions/promotionActionCreators';
import { getUnmappedItems } from '../../redux/actions/resellerActionCreators';
import {
    ClaimCategory,
    ClaimType,
    ClaimWithMetadata,
    CreditMemoStatus,
    ResellerItem,
    RootState,
} from '../../redux/types';
import { color } from '../../theme/colors';
import { DocumentAttachment } from '../../types';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDateIso, formatDateIsoUtc } from '../../utils/dateUtils';
import { PageColumn, useTranslation } from '../blocks';
import { MultiColumnInfoBoxTile } from '../blocks/MultiColumnInfoBoxTile';
import { ClaimDocumentTable } from './ClaimDocumentTable';
import { ClaimDocumentUploader } from './ClaimDocumentUploader';
import { ClaimLineItemList } from './ClaimLineItemList';
import { ClaimReferenceCell } from './ClaimReferenceCell';
import LabeledField from './LabeledField';
import { PromotionalClaimItemList } from './PromotionalClaimItemList';

type ClaimDetailsProps = {
    resellerId: string;
    username: string;
    claimWithMetadata: ClaimWithMetadata;
    resellerItems: ResellerItem[];
    fileId?: string;
};

const ClaimedItemsSection = styled(Column)`
    border: 1px solid ${color.gray[300]};
    margin-bottom: 16px;
`;

const ClaimedItemsHeader = styled.div`
    background-color: ${color.gray[100]};
    padding: 12px 16px;
    border-bottom: 1px solid ${color.gray[300]};
    font-weight: bold;
`;

const ClaimDetails: FC<ClaimDetailsProps & ClaimDetailsReduxProps> = ({
    resellerId,
    username,
    claimWithMetadata,
    fileId,
    resellerItems,
    unmappedItemStatus,
    getUnmappedItems,
    fetchMissingPromotions,
    offlinePromotions,
    asinToImageUrls,
}) => {
    const [documents, setDocuments] = useState<DocumentAttachment[]>(claimWithMetadata.claimMetadata.attachments || []);
    const { t } = useTranslation('claimDetails');
    const { t: tClaimsListPage } = useTranslation('claimsListPage');
    const claimDetails = claimWithMetadata.detailedClaim;
    const downloadDocument = useViewClaimDocument();

    const handleDeleteDocument = (docId: string) => {
        setDocuments((docs) => docs.filter((d) => d.attachmentId !== docId));
    };

    const handleDocumentUploaded = (document: DocumentAttachment) => {
        setDocuments((prev) => [...prev, document]);
    };

    const itemMap = useMemo(() => new Map(resellerItems.map((item) => [item.itemId, item])), [resellerItems]);

    useEffect(() => {
        if (claimDetails) {
            const itemIds = claimDetails.claimLineItems.map((lineItem) => lineItem.itemId);
            const unmappedItemIds = itemIds.filter((itemId) => !itemMap.has(itemId));
            if (unmappedItemIds.length > 0 && unmappedItemStatus === 'Uninitialized') {
                getUnmappedItems(resellerId, unmappedItemIds);
            }
        }
    }, [resellerId, unmappedItemStatus, getUnmappedItems, itemMap, claimWithMetadata.detailedClaim, claimDetails]);

    useEffect(() => {
        if (claimDetails?.claimCategory === ClaimCategory.PROMOTIONAL) {
            const promotionIds = [
                ...new Set(claimDetails.claimLineItems.map((lineItem) => lineItem.promotionId).filter(Boolean)),
            ];
            if (promotionIds.length > 0) {
                fetchMissingPromotions(resellerId, promotionIds);
            }
        }
    }, [claimDetails, resellerId, fetchMissingPromotions]);

    const handleViewClaimDocument = async (uploadId: string) => {
        await downloadDocument(resellerId, uploadId);
    };

    if (!claimDetails) {
        return null;
    }

    return (
        <PageColumn width={'100%'}>
            <MultiColumnInfoBoxTile widths={['grid-3', 'grid-3', 'grid-3', 'grid-3']} title={t('claimDetails-header')}>
                <>
                    <LabeledField label={t('claimNumber-field')} text={t.cognate(claimDetails.resellerClaimNumber)} />
                    <LabeledField label={t('claimCategory-field')} text={t.cognate(claimDetails.claimCategory)} />
                    <LabeledField label={t('claimType-field')} text={t.cognate(claimDetails.claimType)} />
                    {claimDetails.claimType === ClaimType.HARD_DOLLARS && (
                        <LabeledField
                            label={t('claimContract-field')}
                            text={t.cognate(claimDetails.contractId || '')}
                        />
                    )}
                </>
                <>
                    <LabeledField
                        label={t('claimStartDate-field')}
                        text={t.cognate(formatDateIsoUtc(claimDetails.startDate))}
                    />
                    <LabeledField
                        label={t('claimEndDate-field')}
                        text={t.cognate(formatDateIsoUtc(claimDetails.endDate))}
                    />
                    <LabeledField label={t('claimCurrency-field')} text={t.cognate(claimDetails.currency)} />
                    {claimDetails.claimType === ClaimType.HARD_DOLLARS && (
                        <LabeledField
                            label={t('claimExpenseType-field')}
                            text={t.cognate(claimDetails.contractExpenseDescription || '')}
                        />
                    )}
                </>
                <>
                    <LabeledField
                        label={t('claimStatus-field')}
                        text={t('claimStatus-value', { context: claimDetails.status })}
                    />
                    <LabeledField label={t('claimRequestor-field')} text={t.cognate(claimDetails.requestor)} />
                    <LabeledField
                        label={t('claimTotalAmount-field')}
                        text={t.cognate(formatCurrency(claimDetails.claimTotal, claimDetails.currency))}
                    />
                </>
                <>
                    <LabeledField
                        label={t.cognate('Credit Memo Status')}
                        text={tClaimsListPage('creditMemo', {
                            context: claimDetails.creditMemoStatus || CreditMemoStatus.NOT_PROCESSED,
                        })}
                    />
                    <LabeledField
                        label={tClaimsListPage('creditMemoNumber-columnTitle')}
                        text={t.cognate(claimDetails.creditMemoNumber || t('noCreditMemoNumber-text'))}
                    />
                    <LabeledField
                        label={t('claimDateSubmitted-field')}
                        text={t.cognate(formatDateIso(claimDetails.receiptDate))}
                    />
                    {fileId && (
                        <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                            <Text type={'h200'}>{t('claimFile-field')}</Text>
                            <ClaimReferenceCell
                                translatedText={t('downloadUploadedClaimFile-text')}
                                fileId={fileId}
                                callback={handleViewClaimDocument}
                            />
                        </Column>
                    )}
                </>
            </MultiColumnInfoBoxTile>

            <ClaimedItemsSection spacing={'none'}>
                <ClaimedItemsHeader>
                    <Text type={'h300'}>{t('claimedItemsSection-header')}</Text>
                </ClaimedItemsHeader>
                {claimDetails.claimCategory === ClaimCategory.PROMOTIONAL ? (
                    <PromotionalClaimItemList
                        claimDetails={claimDetails}
                        itemMap={itemMap}
                        promotionMap={offlinePromotions.value}
                        asinToImageUrls={asinToImageUrls}
                    />
                ) : (
                    <Column spacingInset={'medium'}>
                        <ClaimLineItemList
                            claimDetails={claimDetails}
                            itemMap={itemMap}
                            isPromotional={false}
                            asinToImageUrls={asinToImageUrls}
                        />
                    </Column>
                )}
            </ClaimedItemsSection>
            <Row widths={['fill', 'fill']} spacing={'medium'} alignmentVertical={'stretch'}>
                <ClaimDocumentUploader
                    resellerClaimNumber={claimDetails.resellerClaimNumber}
                    resellerId={resellerId}
                    username={username}
                    onDocumentUploaded={handleDocumentUploaded}
                />
                <ClaimDocumentTable
                    resellerId={resellerId}
                    resellerClaimNumber={claimWithMetadata.claimMetadata.resellerClaimNumber}
                    documents={documents}
                    onDocumentDeleted={handleDeleteDocument}
                />
            </Row>
        </PageColumn>
    );
};

const mapStateToProps = ({ resellerReducer, promotionReducer }: RootState) => ({
    unmappedItemStatus: resellerReducer.unmappedItemStatus,
    asinToImageUrls: resellerReducer.asinToImageUrls,
    offlinePromotions: promotionReducer.offlinePromotions,
});

const mapDispatchToProps = {
    getUnmappedItems,
    fetchMissingPromotions,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ClaimDetailsReduxProps = ConnectedProps<typeof connector>;

export default connector(ClaimDetails);
